import { createPromise, delay, Waiter } from "common/promises";
import { getSkip } from "common/skipper";
import { Howl, Howler } from "howler";
import log from "loglevel";
let globalUnlocked = false;
const globalUnlockWaiter = new Waiter();
function globalUnlock() {
    globalUnlocked = true;
    globalUnlockWaiter.trigger();
}
Howler.autoUnlock = true;
export class Sound {
    constructor(loopNameMapper) {
        this.loops = {};
        this.loopNameMapper = loopNameMapper;
    }
    static async load({ sheetPath, sprite, loopNameMapper, }) {
        const { promise, resolve, reject } = await createPromise();
        const sound = new Sound(loopNameMapper);
        sound.sheet = new Howl({
            src: [sheetPath],
            format: ["mp3"],
            // html5 를 쓰면 sprite로 재생할 때 앞 뒤가 짤린다.
            // html5: true,
            sprite,
            onload: resolve,
            onloaderror: (a, b) => reject({ a, b }),
            pool: 10,
        });
        if (!globalUnlocked) {
            sound.sheet.once("unlock", () => {
                log.info("sound unlocked");
                globalUnlock();
            });
        }
        await promise;
        return sound;
    }
    play(name, option) {
        let volume = null;
        if ((option === null || option === void 0 ? void 0 : option.volume) != null) {
            volume = option.volume;
        }
        if (!globalUnlocked) {
            return null;
        }
        const id = this.sheet.play(name);
        if (volume != null) {
            this.sheet.volume(volume, id);
        }
        return id;
    }
    stop(id) {
        this.sheet.stop(id);
    }
    dispose() {
        for (const [, loop] of Object.entries(this.loops)) {
            loop.dispose();
        }
        this.loops = {};
        this.sheet.stop();
    }
    async awaitPlay(name, option) {
        let volume = null;
        if ((option === null || option === void 0 ? void 0 : option.volume) != null) {
            volume = option.volume;
        }
        if (!globalUnlocked) {
            return;
        }
        if (getSkip().skipDelay) {
            return await delay(1);
        }
        const { promise, resolve } = await createPromise();
        const id = this.sheet.play(name);
        if (volume != null) {
            this.sheet.volume(volume, id);
        }
        this.sheet.once("end", resolve, id);
        await promise;
    }
    isPlayingLoop(name) {
        return !!this.loops[name];
    }
    playLoop(name, arg) {
        let speed;
        let volume;
        const strLoopName = name;
        if (typeof arg === "number") {
            speed = arg;
        }
        if (typeof arg === "object") {
            speed = arg.speed;
            volume = arg.volume;
        }
        const names = this.loopNameMapper(name);
        if (this.loops[strLoopName]) {
            if (speed != null) {
                this.loops[strLoopName].setSpeed(speed);
            }
            if (volume != null) {
                this.loops[strLoopName].setVolume(volume);
            }
            else {
                this.loops[strLoopName].setVolume(1);
            }
            return;
        }
        const looper = new Looper({
            names,
            howl: this.sheet,
        });
        looper.play({ speed, volume });
        this.loops[strLoopName] = looper;
    }
    stopLoop(name) {
        const strLoopName = name;
        if (this.loops[strLoopName]) {
            this.loops[strLoopName].dispose();
            delete this.loops[strLoopName];
        }
        else {
            throw new Error(`Can't find ${name}`);
        }
    }
}
class Looper {
    constructor({ names, howl }) {
        this.disposed = false;
        this.names = names;
        this.howl = howl;
        this.state = { id: "idle" };
    }
    play(args) {
        this._playLoop(args).catch(console.error);
        // this._playLoop2(args).catch(console.error);
    }
    dispose() {
        this.disposed = true;
        if (this.state.id === "playing") {
            this.howl.stop(this.state.playId);
        }
    }
    setSpeed(speed) {
        if (this.state.id !== "playing") {
            return;
        }
        this.state.speed = speed;
        this.howl.rate(speed, this.state.playId);
    }
    setVolume(volume) {
        if (this.state.id !== "playing") {
            return;
        }
        this.state.volume = volume;
        this.howl.volume(volume, this.state.playId);
    }
    getSpeed() {
        if (this.state.id === "playing") {
            return this.state.speed;
        }
        else {
            return null;
        }
    }
    getVolume() {
        if (this.state.id === "playing") {
            return this.state.volume;
        }
        else {
            return null;
        }
    }
    async _playLoop2({ speed, volume }) {
        var _a, _b;
        const initialSpeed = speed || 1;
        const initialVolume = volume || 1;
        if (!globalUnlocked) {
            await globalUnlockWaiter.wait();
        }
        const id = this.howl.play(this.names[0]);
        this.howl.loop(true, id);
        while (!this.disposed) {
            for (const name of this.names) {
                const strName = name;
                if (this.disposed) {
                    return;
                }
                const { promise, resolve } = await createPromise();
                this.howl.once("end", resolve, id);
                this.state = {
                    id: "playing",
                    name: strName,
                    playId: id,
                    speed: (_a = this.getSpeed()) !== null && _a !== void 0 ? _a : initialSpeed,
                    volume: (_b = this.getVolume()) !== null && _b !== void 0 ? _b : initialVolume,
                };
                this.howl.rate(this.getSpeed(), id);
                this.howl.volume(this.getVolume(), id);
                await promise;
            }
        }
    }
    async _playLoop({ speed, volume }) {
        var _a, _b;
        const initialSpeed = speed || 1;
        const initialVolume = volume || 1;
        if (!globalUnlocked) {
            await globalUnlockWaiter.wait();
        }
        while (!this.disposed) {
            for (const name of this.names) {
                const strName = name;
                if (this.disposed) {
                    return;
                }
                const id = this.howl.play(strName);
                const { promise, resolve } = await createPromise();
                this.howl.once("end", resolve, id);
                this.state = {
                    id: "playing",
                    name: strName,
                    playId: id,
                    speed: (_a = this.getSpeed()) !== null && _a !== void 0 ? _a : initialSpeed,
                    volume: (_b = this.getVolume()) !== null && _b !== void 0 ? _b : initialVolume,
                };
                this.howl.rate(this.getSpeed(), id);
                this.howl.volume(this.getVolume(), id);
                await promise;
            }
        }
    }
}
//# sourceMappingURL=sound.js.map